// src/pages/KarigarManagement/Karigar/KarigarEditPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKarigarManagement } from "@/hooks/useKarigar";
import { useSelector } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import { Textarea } from "@/components/ui-v2/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Checkbox } from "@/components/ui-v2/checkbox";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";

// Skill categories
const SKILL_CATEGORIES = [
  "GOLD_MAKING",
  "SILVER_MAKING",
  "DIAMOND_SETTING",
  "STONE_SETTING",
  "KUNDAN_WORK",
  "MEENAKARI",
  "POLISHING",
  "ENGRAVING",
  "CHAIN_MAKING",
  "CASTING",
  "WAX_MODELING",
  "REPAIR",
  "RESIZING",
  "RHODIUM_PLATING",
];

const PROFICIENCY_LEVELS = [
  "BEGINNER",
  "INTERMEDIATE",
  "ADVANCED",
  "EXPERT",
  "MASTER",
];

const WORK_TYPES = [
  "NEW_MAKING",
  "REPAIR",
  "CUSTOMIZATION",
  "ALTERATION",
  "POLISHING",
  "STONE_REPLACEMENT",
];

const KarigarEditPage = () => {
  const navigate = useNavigate();
  const { karigarId } = useParams();

  const {
    currentKarigar,
    loading,
    error,
    successMessage,
    getKarigarById,
    editKarigar,
    clearError,
    clearSuccessMessage,
    clearCurrentKarigar,
  } = useKarigarManagement();

  const { branches } = useSelector((state) => state.branch);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const {
    fields: skillFields,
    append: appendSkill,
    remove: removeSkill,
  } = useFieldArray({
    control,
    name: "skills",
  });

  // Fetch karigar details
  useEffect(() => {
    if (karigarId) {
      getKarigarById(karigarId);
    }
    return () => {
      clearCurrentKarigar();
    };
  }, [karigarId]);

  // Populate form when karigar is loaded
  useEffect(() => {
    if (currentKarigar) {
      reset({
        name: currentKarigar.name || "",
        phoneNumber: currentKarigar.phoneNumber || "",
        email: currentKarigar.email || "",
        address: {
          street: currentKarigar.address?.street || "",
          city: currentKarigar.address?.city || "",
          state: currentKarigar.address?.state || "",
          pincode: currentKarigar.address?.pincode || "",
        },
        skills: currentKarigar.skills?.length
          ? currentKarigar.skills.map((s) => ({
              category: s.category,
              proficiencyLevel: s.proficiencyLevel,
              experienceYears: s.experienceYears,
            }))
          : [
              {
                category: "",
                proficiencyLevel: "INTERMEDIATE",
                experienceYears: 1,
              },
            ],
        workPreferences: {
          maxConcurrentJobs:
            currentKarigar.workPreferences?.maxConcurrentJobs || 5,
          preferredWorkTypes:
            currentKarigar.workPreferences?.preferredWorkTypes || [],
        },
        bankDetails: {
          accountHolderName:
            currentKarigar.bankDetails?.accountHolderName || "",
          accountNumber: currentKarigar.bankDetails?.accountNumber || "",
          bankName: currentKarigar.bankDetails?.bankName || "",
          ifscCode: currentKarigar.bankDetails?.ifscCode || "",
        },
      });
    }
  }, [currentKarigar, reset]);

  // Handle messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      navigate(`/karigar-management/karigars/${karigarId}`);
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // Handle form submit
  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Filter out empty skills
      data.skills = data.skills.filter((s) => s.category);

      await editKarigar(karigarId, data);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Edit Karigar</h1>
          <p className="text-muted-foreground">
            {currentKarigar?.name} - {currentKarigar?.karigarCode}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Personal details of the karigar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Name is required" })}
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  {...register("phoneNumber", {
                    required: "Phone number is required",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "Invalid phone number",
                    },
                  })}
                  placeholder="10 digit phone number"
                />
                {errors.phoneNumber && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  placeholder="Email address (optional)"
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>Address</CardTitle>
              <CardDescription>Residential address details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="street">Street Address</Label>
                <Textarea
                  id="street"
                  {...register("address.street")}
                  placeholder="Street address"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    {...register("address.city", {
                      required: "City is required",
                    })}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    {...register("address.state", {
                      required: "State is required",
                    })}
                    placeholder="State"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pincode">Pincode</Label>
                <Input
                  id="pincode"
                  {...register("address.pincode")}
                  placeholder="6 digit pincode"
                />
              </div>
            </CardContent>
          </Card>

          {/* Skills */}
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
              <CardDescription>Karigar's expertise areas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {skillFields.map((field, index) => (
                <div key={field.id} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label>Category</Label>
                    <Select
                      value={watch(`skills.${index}.category`)}
                      onValueChange={(value) =>
                        setValue(`skills.${index}.category`, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select skill" />
                      </SelectTrigger>
                      <SelectContent>
                        {SKILL_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label>Level</Label>
                    <Select
                      value={watch(`skills.${index}.proficiencyLevel`)}
                      onValueChange={(value) =>
                        setValue(`skills.${index}.proficiencyLevel`, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROFICIENCY_LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label>Years</Label>
                    <Input
                      type="number"
                      min={0}
                      {...register(`skills.${index}.experienceYears`)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSkill(index)}
                    disabled={skillFields.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  appendSkill({
                    category: "",
                    proficiencyLevel: "INTERMEDIATE",
                    experienceYears: 1,
                  })
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Skill
              </Button>
            </CardContent>
          </Card>

          {/* Work Preferences */}
          <Card>
            <CardHeader>
              <CardTitle>Work Preferences</CardTitle>
              <CardDescription>Work capacity and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="maxJobs">Max Concurrent Jobs</Label>
                <Input
                  id="maxJobs"
                  type="number"
                  min={1}
                  max={20}
                  {...register("workPreferences.maxConcurrentJobs")}
                />
              </div>

              <div>
                <Label>Preferred Work Types</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {WORK_TYPES.map((type) => (
                    <div key={type} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-${type}`}
                        checked={watch(
                          "workPreferences.preferredWorkTypes"
                        )?.includes(type)}
                        onCheckedChange={(checked) => {
                          const current =
                            watch("workPreferences.preferredWorkTypes") || [];
                          if (checked) {
                            setValue("workPreferences.preferredWorkTypes", [
                              ...current,
                              type,
                            ]);
                          } else {
                            setValue(
                              "workPreferences.preferredWorkTypes",
                              current.filter((t) => t !== type)
                            );
                          }
                        }}
                      />
                      <Label
                        htmlFor={`edit-${type}`}
                        className="text-sm cursor-pointer"
                      >
                        {type.replace(/_/g, " ")}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Bank Details</CardTitle>
              <CardDescription>For payment processing</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="accountHolderName">Account Holder Name</Label>
                  <Input
                    id="accountHolderName"
                    {...register("bankDetails.accountHolderName")}
                    placeholder="Name as per bank"
                  />
                </div>
                <div>
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Input
                    id="accountNumber"
                    {...register("bankDetails.accountNumber")}
                    placeholder="Bank account number"
                  />
                </div>
                <div>
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    {...register("bankDetails.bankName")}
                    placeholder="Bank name"
                  />
                </div>
                <div>
                  <Label htmlFor="ifscCode">IFSC Code</Label>
                  <Input
                    id="ifscCode"
                    {...register("bankDetails.ifscCode")}
                    placeholder="IFSC code"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || loading.update}>
            <Save className="h-4 w-4 mr-2" />
            {submitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default KarigarEditPage;
